import {
  ChevronLeft as ChevronLeftIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../api/apiSlice";
import { downloadIftaReport, IFTA_JOB_SESSION_KEY } from "../../api/reportsApi";
import { useGetSubscriptionLevelQuery } from "../../api/subscriptionApi";
import { useLogoutMutation } from "../../api/authApi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useSignalR } from "../../contexts/SignalRContext";
import { logout, selectRefreshToken } from "../../features/auth/authSlice";
import { setVehicles } from "../../features/vehicles/vehiclesSlice";
import { useTheme as useAppTheme } from "../../theme/ThemeContext";
import { getVisibleNavItems, groupNavItems, isNavPathActive } from "./navMenu";
import SidebarNavSection, { SidebarNavItem } from "./SidebarNavSection";

const DRAWER_WIDTH = 220;
const COLLAPSED_DRAWER_WIDTH = 56;
const ADUT_LOGO = "/images/adut-logo.svg";

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const token = useAppSelector((state) => state.auth.token);
  const refreshToken = useAppSelector(selectRefreshToken);
  const [logoutSession] = useLogoutMutation();
  const [isOpen, setIsOpen] = useState(true);
  const { mode, toggleTheme } = useAppTheme();
  const { data: subscriptionLevelData } = useGetSubscriptionLevelQuery(
    undefined,
    { skip: !user, refetchOnMountOrArgChange: true },
  );
  const subscriptionLevel = subscriptionLevelData?.level ?? null;
  const { connection } = useSignalR();

  const [iftaSnackbar, setIftaSnackbar] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });

  const handleIftaReady = useCallback(
    async (payload: { jobId: string; quarter: string }) => {
      const stored = sessionStorage.getItem(IFTA_JOB_SESSION_KEY);
      if (!stored) return;
      let job: { jobId: string; quarter: string };
      try {
        job = JSON.parse(stored);
      } catch {
        return;
      }
      if (job.jobId !== payload.jobId) return;

      sessionStorage.removeItem(IFTA_JOB_SESSION_KEY);
      dispatch(api.util.invalidateTags(["IftaGenerations"]));
      try {
        await downloadIftaReport(payload.jobId, payload.quarter, token ?? "");
        setIftaSnackbar({
          open: true,
          severity: "success",
          message: `IFTA Report (${payload.quarter}) downloaded successfully.`,
        });
      } catch {
        setIftaSnackbar({
          open: true,
          severity: "error",
          message:
            "Report is ready but download failed. Please try again from the IFTA page.",
        });
      } finally {
        sessionStorage.removeItem(IFTA_JOB_SESSION_KEY);
      }
    },
    [token, dispatch],
  );

  const handleIftaFailed = useCallback(
    (payload: { jobId: string; error: string }) => {
      const stored = sessionStorage.getItem(IFTA_JOB_SESSION_KEY);
      if (!stored) return;
      let job: { jobId: string };
      try {
        job = JSON.parse(stored);
      } catch {
        return;
      }
      if (job.jobId !== payload.jobId) return;

      sessionStorage.removeItem(IFTA_JOB_SESSION_KEY);
      dispatch(api.util.invalidateTags(["IftaGenerations"]));
      setIftaSnackbar({
        open: true,
        severity: "error",
        message:
          payload.error ||
          "IFTA report generation failed. Contact support@adut.us if this keeps happening.",
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (!connection) return;
    connection.on("IftaReportReady", handleIftaReady);
    connection.on("IftaReportFailed", handleIftaFailed);
    return () => {
      connection.off("IftaReportReady", handleIftaReady);
      connection.off("IftaReportFailed", handleIftaFailed);
    };
  }, [connection, handleIftaReady, handleIftaFailed]);

  // Auto-collapse drawer on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    if (refreshToken) {
      try {
        await logoutSession({ refreshToken }).unwrap();
      } catch {
        // Clear local auth even if the API call fails
      }
    }

    dispatch(api.util.resetApiState());
    dispatch(setVehicles([]));
    dispatch(logout());
    navigate("/login");
  };

  const visibleMenuItems = getVisibleNavItems(user, subscriptionLevel);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        maxWidth: "100vw",
        overflow: "hidden",
      }}
    >
      {/* Main Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            maxWidth: isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: "none",
            boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: "hidden",
            overflowX: "hidden",
            overflowY: "hidden",
            zIndex: 1200,
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            overflowX: "hidden",
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: isOpen ? "space-between" : "center",
              gap: 1,
              overflow: "hidden",
            }}
          >
            {isOpen && (
              <img
                src={ADUT_LOGO}
                alt="ADUT Global"
                style={{
                  height: "28px",
                  width: "auto",
                  maxWidth: 140,
                  minWidth: 0,
                  flexShrink: 1,
                }}
                onError={(e) => {
                  e.currentTarget.src =
                    "https://adut.us/wp-content/uploads/2023/12/adut-us-logo.svg";
                  e.currentTarget.style.height = "28px";
                }}
              />
            )}
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                flexShrink: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.2rem",
                },
              }}
            >
              {isOpen ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          </Box>

          <Divider sx={{ opacity: 0.6 }} />

          {/* Navigation Menu */}
          <List
            sx={{
              flexGrow: 1,
              minHeight: 0,
              minWidth: 0,
              py: 0.25,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {groupNavItems(visibleMenuItems).map((group) => {
              if (group.kind === "section") {
                return (
                  <SidebarNavSection
                    key={group.label.path || group.label.text}
                    label={group.label}
                    items={group.items}
                    sidebarOpen={isOpen}
                    pathname={location.pathname}
                    onNavigate={navigate}
                  />
                );
              }

              const item = group.item;
              if (!item.path) return null;
              return (
                <SidebarNavItem
                  key={item.text}
                  item={item}
                  selected={isNavPathActive(location.pathname, item.path)}
                  showText={isOpen}
                  onClick={() => navigate(item.path!)}
                />
              );
            })}
          </List>

          {/* Theme Toggle */}
          <Box
            sx={{
              px: 1,
              py: 0.75,
              mt: "auto",
              minWidth: 0,
              overflowX: "hidden",
            }}
          >
            <ListItem
              button
              onClick={toggleTheme}
              sx={{
                minHeight: 30,
                justifyContent: isOpen ? "initial" : "center",
                px: 1.25,
                borderRadius: 1,
                mb: 0.5,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isOpen ? 1.25 : "auto",
                  justifyContent: "center",
                  color: "inherit",
                  "& .MuiSvgIcon-root": {
                    fontSize: "1rem",
                  },
                }}
              >
                {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
              </ListItemIcon>
              {isOpen && (
                <ListItemText
                  primary={mode === "light" ? "Dark Mode" : "Light Mode"}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              )}
            </ListItem>

            {/* User Section */}
            <Divider sx={{ opacity: 0.6, mb: 0.5 }} />
            <Box sx={{ p: 0.5, mt: "auto" }}>
              {isOpen ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    mb: 0.5,
                    cursor: "pointer",
                    borderRadius: 1,
                    p: 0.75,
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => navigate("/profile")}
                  title="Click to edit profile"
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        mode === "light" ? "primary.main" : "primary.light",
                      mr: 1,
                      width: 24,
                      height: 24,
                      fontSize: "0.75rem",
                      flexShrink: 0,
                    }}
                  >
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.7rem",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user?.firstName} {user?.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: "0.65rem",
                        display: "block",
                        lineHeight: 1.1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 0.5,
                    cursor: "pointer",
                    borderRadius: 1,
                    p: 0.75,
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => navigate("/profile")}
                  title="Click to edit profile"
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        mode === "light" ? "primary.main" : "primary.light",
                      width: 24,
                      height: 24,
                      fontSize: "0.75rem",
                    }}
                  >
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </Avatar>
                </Box>
              )}
              <ListItem
                button
                onClick={handleLogout}
                sx={{
                  minHeight: 30,
                  justifyContent: isOpen ? "initial" : "center",
                  px: 1.25,
                  borderRadius: 1,
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isOpen ? 1.25 : "auto",
                    justifyContent: "center",
                    color: "inherit",
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                {isOpen && (
                  <ListItemText
                    primary="Logout"
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                )}
              </ListItem>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100vh",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          overflowX: "hidden",
          transition: theme.transitions.create("margin-left", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        }}
      >
        {children}
      </Box>

      <Snackbar
        open={iftaSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setIftaSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={iftaSnackbar.severity}
          onClose={() => setIftaSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {iftaSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppLayout;
